/**
 * The shared drill library — the pure half.
 *
 * Publishing turns a team drill into a catalog drill: only the parts that
 * make sense on another team survive. Adding turns a catalog drill back into
 * an ordinary team drill with its own id. Both directions are here so the
 * rules can be tested without a database.
 */
import type { DrillData, DrillDiagramEntry, PlayData, PlayerRow } from '../types';
import { BUILTIN_TYPES } from '../plan/types';
import { buildDrillSVG } from '../playbook/svg';
import { drillDiagrams, withDiagrams } from '../playbook/drills';

export interface CatalogDrillRow {
  id: string;
  name: string;
  cat: string;
  data: DrillData;
  starter: boolean;
  source_team_id: string | null;
  source_item_id: string | null;
  published_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export const BUILTIN_TYPE_CODES = BUILTIN_TYPES.map((t) => t.code);

/** Period type other teams will have. A custom code (e.g. "TK") exists only
 *  on the team that made it, and the period picker filters drills by type —
 *  so a shared drill has to carry a built-in one. */
export function portableType(type: string | undefined): string | null {
  if (!type) return null;
  return BUILTIN_TYPE_CODES.includes(type) ? type : null;
}

const hasEmbeddedImage = (svg: string) => /<image\b/i.test(svg) || /data:image\//i.test(svg);

export interface PublishPlan {
  data: DrillData;
  /** What was left out, in plain words, for the publish dialog. */
  dropped: string[];
}

/**
 * The drill as it will appear in the shared library.
 *  - photos, PDFs and uploaded media never go (team-private storage; photos
 *    can show players)
 *  - a diagram with a picture embedded in it is left out; drawn geometry is
 *    kept and its SVG rebuilt from the geometry
 *  - the link to the publisher's playbook (playId) is dropped
 *  - type must be built-in (pass the one chosen in the dialog)
 */
export function toCatalogDrill(d: DrillData, type: string | null): PublishPlan {
  const dropped: string[] = [];
  const out: DrillData = {
    id: '',
    name: (d.name || '').trim(),
    cat: (d.cat || '').trim(),
    ...(d.duration ? { duration: d.duration } : {}),
    ...(d.desc?.trim() ? { desc: d.desc.trim() } : {}),
    ...(d.steps?.some((s) => s.trim()) ? { steps: d.steps.map((s) => s.trim()).filter(Boolean) } : {}),
    ...(d.groups?.length ? { groups: [...d.groups] } : {}),
  };
  const t = type ?? portableType(d.type);
  if (t) out.type = t;

  const keep: DrillDiagramEntry[] = [];
  let pictures = 0;
  for (const g of drillDiagrams(d)) {
    const label = g.label ? { label: g.label } : {};
    if (g.play) keep.push({ svg: buildDrillSVG(g.play), play: JSON.parse(JSON.stringify(g.play)) as PlayData, cardMode: g.play.cardMode, ...label });
    else if (g.svg && !hasEmbeddedImage(g.svg)) keep.push({ svg: g.svg, ...label });
    else pictures++;
  }
  if (keep.length) Object.assign(out, withDiagrams(out, keep));
  if (pictures) dropped.push(pictures === 1 ? 'a diagram (it has a picture embedded in it)' : `${pictures} diagrams (they have pictures embedded in them)`);
  const photos = (d.photos?.length ?? 0) + (d.media?.filter((m) => m.kind === 'photo').length ?? 0);
  if (photos) dropped.push(photos === 1 ? '1 photo' : `${photos} photos`);
  if (d.pdfAttachment?.data || d.media?.some((m) => m.kind === 'pdf')) dropped.push('the PDF');
  return { data: out, dropped };
}

/** A team copy of a catalog drill: fresh id, remembers where it came from. */
export function fromCatalogDrill(c: Pick<CatalogDrillRow, 'id' | 'data'>, newId: string): DrillData {
  const d = JSON.parse(JSON.stringify(c.data)) as DrillData;
  return { ...d, id: newId, catalogId: c.id };
}

/**
 * Roster names that appear in what would be shared — a nudge before
 * publishing, since notes like "Zeke holds the bag" name a child. Matches whole
 * words, first or last names of 3+ letters, case-insensitive.
 */
export function namesMentioned(d: DrillData, roster: Pick<PlayerRow, 'first_name' | 'last_name'>[]): string[] {
  const text = [d.name, d.desc ?? '', ...(d.steps ?? []), ...drillDiagrams(d).flatMap((g) => [g.label, ...(g.play?.players.map((p) => p.label ?? '') ?? [])])].join('\n');
  const found = new Set<string>();
  for (const p of roster) {
    for (const n of [p.first_name, p.last_name]) {
      const w = (n || '').trim();
      if (w.length < 3) continue;
      const re = new RegExp(`(^|[^\\p{L}])${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^\\p{L}])`, 'iu');
      if (re.test(text)) found.add(w);
    }
  }
  return [...found].sort();
}

/** Search: every word must appear in the name, category, description or steps. */
export function searchCatalog(rows: CatalogDrillRow[], q: string, cat = 'All'): CatalogDrillRow[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  return rows
    .filter((r) => !r.deleted_at)
    .filter((r) => cat === 'All' || (r.cat || 'Other') === cat)
    .filter((r) => {
      if (!words.length) return true;
      const hay = [r.name, r.cat, r.data.desc ?? '', ...(r.data.steps ?? []), ...(r.data.groups ?? [])].join(' ').toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
